import { generateInitialValues } from './initialValuesGenerator';
import { OptionalObjectSchema } from 'yup/lib/object';
import { useEffect, useState } from 'react';
import { JsonForm } from 'components/UI/JsonForm';
import { yupResolver } from '@hookform/resolvers/yup';

import MultiStepHeader from './MultiStepHeader';
import ApplicationFormSections from './ApplicationFormSections';
import Button from 'components/UI/Button';
import {
  EFormConfigType,
  IApplicationFormStep,
} from 'features/ApplicationForm/types';
import {
  ApplicationFormProvider,
  useApplicationFormContext,
} from 'features/ApplicationForm/ApplicationFormProvider';
import {
  formConfigurationMapping,
  standardSteps,
  standardStepSchema,
} from 'features/ApplicationForm/FormConfig';
import SubmitSuccess from './SubmitSuccess';
import { useApplicationForm } from 'hooks/ReactQueryHooks/useApplicationFromQuery';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IPostApplicationServicePayload } from 'api/Rover/ApplicationForm/types';
import {
  getApplicationFormInfo,
  postApplication,
} from 'api/Rover/ApplicationForm';
import PageNotFound from 'pages/PageNotFound';
import Loading from 'components/UI/loading';

interface IApplicationFormBaseProps {
  applicationFormId: string;
  steps: IApplicationFormStep[];
  initialValues?: Record<string, any>;
  validationSchema?: OptionalObjectSchema<any, any, any>[];
  onSuccess?: (
    data?: unknown,
    variables?: void,
    context?: unknown | undefined
  ) => Promise<unknown> | void;
}

const ApplicationFormBase = (props: IApplicationFormBaseProps) => {
  const { applicationFormId, steps, validationSchema, initialValues } = props;

  const {
    currentStep,
    setCurrentStep,
    completedSteps,
    updateData,
    data,
    setFormJSON,
  } = useApplicationFormContext();

  useEffect(() => {
    if (steps) {
      setFormJSON(steps);
    }
    return () => {
      setFormJSON([]);
    };
  }, [steps]);

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const increaseStep = () => {
    if (!isLast) {
      setCurrentStep((prev) => prev + 1);
    }
  };
  const decreaseStep = () => {
    if (!isFirst) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleNext = (values: any) => {
    updateData(values);
    increaseStep();
  };

  const submitApplication = useMutation(
    (data: IPostApplicationServicePayload) => postApplication({ data }),
    {
      onSuccess: (data: any) => {
        if (props.onSuccess) {
          props.onSuccess();
        }
      },
    }
  );

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      handleNext(values);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: applicationFormId,
        contactName: values.applicantInfo.fullName,
        contactEmail: values.applicantInfo.contactEmail,
        applicationData: values,
      };
      submitApplication.mutate(applicationData);
    }
  };

  const currentFormStep = steps[currentStep];

  return (
    <>
      <div className='container w-full h-screen/80 mx-auto p-5 flex flex-col justify-between'>
        <MultiStepHeader steps={steps} currentStep={currentStep} />
        <div className='flex-1 space-y-6 lg:col-span-9 overflow-y-scroll'>
          <JsonForm
            onSubmit={onSubmitHandler}
            hookFormProviderProps={{
              defaultValues: initialValues,
              mode: 'all',
              resolver:
                validationSchema &&
                validationSchema[currentStep] &&
                yupResolver(validationSchema[currentStep]),
            }}
            formClassName='p-4'
          >
            {currentFormStep && (
              <div className='col-span-12 grid grid-cols-12 gap-4 w-full h-full'>
                <ApplicationFormSections
                  step={currentFormStep}
                  currentStep={currentStep}
                />
              </div>
            )}
            <div className='col-span-12 flex justify-end space-x-4 py-4 border-t'>
              {!isFirst && (
                <Button
                  label='Previous'
                  btnStyle='neutral'
                  type='button'
                  onClick={(e) => {
                    e.preventDefault();
                    decreaseStep();
                  }}
                />
              )}
              <Button
                label={`${isLast ? 'Submit' : 'Next'}`}
                btnStyle='primary'
                type='submit'
              />
            </div>
          </JsonForm>
        </div>
      </div>
    </>
  );
};

export const ApplicationForm = () => {
  const { applicationFormId } = useParams<string>();
  const [completed, setCompleted] = useState<boolean>(false);
  const [showInitialPage, setShowInitialPage] = useState(true);

  const fetchApplicationForm = useQuery(['GET_APPLICATION_FORM_INFO'], () =>
    getApplicationFormInfo({ applicationFormId: applicationFormId! })
  );
  const applicationForm = fetchApplicationForm?.data?.data;

  if (fetchApplicationForm.isLoading) {
    return <Loading loadingText='Loading' />;
  }

  if (!applicationForm || !applicationForm?.active) {
    return <PageNotFound />;
  }

  const formConfig = formConfigurationMapping.find(
    (fcm) => fcm.formCode === applicationForm.templateCode
  );

  if (!formConfig) {
    return (
      <PageNotFound
        title='Oops! Under Construction'
        subtitle='This page you are looking for is under construction. Please visit us again later.'
      />
    );
  }

  if (
    formConfig.type === EFormConfigType.FREE &&
    formConfig.applicationFormComponent
  ) {
    return (
      <formConfig.applicationFormComponent
        formData={fetchApplicationForm?.data}
      />
    );
  }

  if (formConfig?.applicationFormInitialComponent && showInitialPage) {
    return (
      <formConfig.applicationFormInitialComponent
        startApplicationHandler={() => setShowInitialPage(false)}
      />
    );
  }

  if (completed) {
    return <SubmitSuccess message={applicationForm?.confirmationMessage} />;
  }

  let formSteps = [...standardSteps];
  if (formConfig.formSteps) {
    formSteps = [...standardSteps, ...formConfig.formSteps];
  }

  let formSchema: any[] = [];

  if (formConfig.validationSchema) {
    formSchema = [standardStepSchema, ...formConfig.validationSchema];
  }

  const initialValues: { [key: string]: any } =
    generateInitialValues(formSteps);

  return (
    <div className='flex flex-col justify-between'>
      <div className='container mx-auto p-5'>
        <h2 className='mb-0'>{applicationForm?.name}</h2>
      </div>
      <div className='bg-white h-full'>
        <ApplicationFormProvider initialValues={initialValues}>
          <ApplicationFormBase
            applicationFormId={applicationFormId!}
            steps={formSteps}
            validationSchema={formSchema}
            initialValues={initialValues}
            onSuccess={() => setCompleted(true)}
          />
        </ApplicationFormProvider>
      </div>
    </div>
  );
};
